import {
  type MpocContact,
  type MpocFundingRequestTask,
  useCompleteTaskMutation,
} from '@cartwheel/api/v1'
import { Button } from '@cartwheel/components'
import { SectionDivider, TaskDescription } from './shared/task-workspace-primitives'
import { useTaskCompletion } from './shared/use-task-completion'
import { TaskCompletionBanner } from './task-completion-banner'

const BLOCKED_REASON_LABEL: Record<string, string> = {
  no_pro_bono_slots_remaining: 'No pro bono slots remaining for this district',
  district_opted_out: 'District has opted out of automated approval emails',
  no_mpoc_contacts: 'No MPOC contact on file for this district',
  pro_bono_slot_data_unavailable: 'Pro bono slot data is unavailable for this district',
}

function BlockedReasonBanner({ reason }: { reason: string | null | undefined }) {
  const label = reason ? (BLOCKED_REASON_LABEL[reason] ?? reason) : null

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold text-charcoal">Why the request wasn't sent</h3>
      <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
        <p className="text-sm leading-[1.5] text-charcoal-50">
          {label ?? 'The reason could not be determined.'}
        </p>
      </div>
    </section>
  )
}

function ContactCard({ contact }: { contact: MpocContact }) {
  return (
    <div className="rounded-lg border border-sand bg-white p-4 shadow-sm space-y-1">
      {contact.name && <p className="text-sm font-semibold text-charcoal">{contact.name}</p>}
      {contact.email ? (
        <p className="text-sm font-mono text-charcoal-50 select-all">{contact.email}</p>
      ) : (
        <p className="text-sm text-charcoal-50 italic">No email on file</p>
      )}
    </div>
  )
}

function MpocContactsSection({
  contacts,
  organizationName,
}: {
  contacts: Array<MpocContact>
  organizationName: string | null | undefined
}) {
  const heading = organizationName ? `MPOC contacts — ${organizationName}` : 'MPOC contacts'

  if (contacts.length === 0) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-charcoal">{heading}</h3>
        <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
          <p className="text-sm leading-[1.5] text-charcoal-50">
            No MPOC contacts are on file for this district.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold text-charcoal">{heading}</h3>
      <p className="text-sm text-charcoal-50">
        Email each contact below to request financial aid approval.
      </p>
      <div className="space-y-2">
        {contacts.map((contact, i) => (
          <ContactCard key={contact.email ?? i} contact={contact} />
        ))}
      </div>
    </section>
  )
}

export function MpocFundingRequestWorkspace({
  task,
  canWork = true,
  onClose,
}: {
  task: MpocFundingRequestTask
  canWork?: boolean
  onClose: () => void
}) {
  const isSettled = task.status === 'completed' || task.status === 'canceled'
  const { onSuccess, onError } = useTaskCompletion({ taskId: task.id, onClose })
  const { mutate: complete, isPending } = useCompleteTaskMutation({
    onSuccess: () => onSuccess('Task marked complete'),
    onError: () => onError('Failed to complete task'),
  })

  return (
    <div className="space-y-8">
      <TaskCompletionBanner task={task} />
      <TaskDescription task={task} />

      {!isSettled && (
        <>
          <SectionDivider />
          <BlockedReasonBanner reason={task.blocked_reason} />
          {canWork && (
            <>
              <SectionDivider />
              <MpocContactsSection
                contacts={task.mpoc_contacts ?? []}
                organizationName={task.organization_name}
              />
              <Button onClick={() => complete({ path: { id: task.id } })} disabled={isPending}>
                {isPending ? 'Saving…' : 'Mark complete'}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  )
}
